import {
  ArchiveBoxIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { CallListStatus } from "../../../models/Lead";
import classNames from "../../../utils/classNames";

export const QUOTE_CALL_STAGES = [
  {
    key: "awaiting_contact",
    label: "Awaiting contact",
    icon: PhoneIcon,
    color: "amber" as const,
    callListStatus: null,
    isNullFilter: true,
  },
  {
    key: "call_list_one",
    label: "1st call",
    icon: PhoneIcon,
    color: "blue" as const,
    callListStatus: CallListStatus.CallListOne,
    isNullFilter: false,
  },
  {
    key: "email_list_one",
    label: "1st email",
    icon: EnvelopeIcon,
    color: "indigo" as const,
    callListStatus: CallListStatus.EmailListOne,
    isNullFilter: false,
  },
  {
    key: "call_list_two",
    label: "2nd call",
    icon: PhoneIcon,
    color: "blue" as const,
    callListStatus: CallListStatus.CallListTwo,
    isNullFilter: false,
  },
  {
    key: "email_list_two",
    label: "2nd email",
    icon: EnvelopeIcon,
    color: "indigo" as const,
    callListStatus: CallListStatus.EmailListTwo,
    isNullFilter: false,
  },
  {
    key: "call_list_three",
    label: "3rd call",
    icon: PhoneIcon,
    color: "blue" as const,
    callListStatus: CallListStatus.CallListThree,
    isNullFilter: false,
  },
  {
    key: "email_list_three",
    label: "Final email",
    icon: EnvelopeIcon,
    color: "rose" as const,
    callListStatus: CallListStatus.EmailListThree,
    isNullFilter: false,
  },
  {
    key: "manager_approval",
    label: "Manager approval",
    icon: ShieldCheckIcon,
    color: "purple" as const,
    callListStatus: CallListStatus.ManagerApproval,
    isNullFilter: false,
  },
  {
    key: "archived",
    label: "Archived",
    icon: ArchiveBoxIcon,
    color: "gray" as const,
    callListStatus: CallListStatus.Archived,
    isNullFilter: false,
  },
] as const;

export type QuoteCallStageKey = (typeof QUOTE_CALL_STAGES)[number]["key"];

type StageColor = (typeof QUOTE_CALL_STAGES)[number]["color"];

export const colorMap: Record<
  StageColor,
  { bg: string; text: string; icon: string; ring: string; badge: string }
> = {
  amber: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    icon: "text-amber-500",
    ring: "ring-amber-200",
    badge: "bg-amber-600",
  },
  blue: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    icon: "text-blue-500",
    ring: "ring-blue-200",
    badge: "bg-blue-600",
  },
  indigo: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    icon: "text-indigo-500",
    ring: "ring-indigo-200",
    badge: "bg-indigo-600",
  },
  rose: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    icon: "text-rose-500",
    ring: "ring-rose-200",
    badge: "bg-rose-600",
  },
  purple: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    icon: "text-purple-500",
    ring: "ring-purple-200",
    badge: "bg-purple-600",
  },
  gray: {
    bg: "bg-gray-50",
    text: "text-gray-600",
    icon: "text-gray-400",
    ring: "ring-gray-200",
    badge: "bg-gray-500",
  },
};

export default function QuoteCallStageFilter({
  setActiveStageFilter,
  activeStageFilter,
  totalQuotes,
  stageCounts = {},
}: {
  setActiveStageFilter: (stage: QuoteCallStageKey | null) => void;
  activeStageFilter: QuoteCallStageKey | null;
  totalQuotes?: number;
  stageCounts?: Partial<Record<QuoteCallStageKey, number>>;
}) {
  const { t } = useTranslation("leads");
  const computedTotalQuotes = totalQuotes ?? Object.values(stageCounts).reduce((sum, value) => sum + (value ?? 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">{t("Quotes by Call Stage")}</h3>
        <span className="text-sm text-gray-500">{t("{{count}} total", { count: computedTotalQuotes })}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-9 gap-3">
        {QUOTE_CALL_STAGES.map((stage) => {
          const Icon = stage.icon;
          const count = stageCounts[stage.key] ?? 0;
          const c = colorMap[stage.color];
          const isActive = activeStageFilter === stage.key;

          return (
            <button
              key={stage.key}
              type="button"
              onClick={() => setActiveStageFilter(isActive ? null : stage.key)}
              className={classNames(
                "relative rounded-xl border p-4 text-left transition-all hover:shadow-md",
                isActive
                  ? `${c.bg} border-current ring-2 ${c.ring}`
                  : "border-gray-200 hover:border-gray-300"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={classNames(
                    "flex h-8 w-8 items-center justify-center rounded-lg",
                    isActive ? c.bg : "bg-gray-100"
                  )}
                >
                  <Icon className={classNames("h-4.5 w-4.5", c.icon)} />
                </div>
                <span className={classNames("text-xs font-medium", isActive ? c.text : "text-gray-500")}>
                  {t(stage.label)}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={classNames("text-3xl font-bold", c.text)}>{count}</span>
                <span className="text-xs text-gray-400">{t("vehicles")}</span>
              </div>
              {isActive && (
                <div
                  className={classNames(
                    "absolute top-2 right-2 h-2.5 w-2.5 rounded-full",
                    c.badge
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}